import {TextField} from '@mui/material'
import './_textInput.scss'

/**
 * TextInput component that renders a MUI TextField with properties from props.
 *
 * @param {Object} props - The properties object.
 * @param {string|number} [props.value] - The value of the text input.
 * @param {Function} [props.onChange] - The function to handle input change events.
 * @param {string} [props.label] - The label text for the input.
 * @param {boolean} [props.required=false] - Optional boolean to indicate if the input is required.
 * @param {boolean} [props.disabled=false] - Optional boolean to disable the input.
 * @param {number} [props.maxLength] - Optional maximum length of the input value.
 * @param {string} [props.type='text'] - Optional type of the input.
 * @param {string} [props.error] - Optional error message to display.
 * @param {string} [props.placeholderText=''] - Optional placeholder text for the input.
 * @param {string} [props.helperText] - Optional helper text to display below the input.
 * @param {Function} [props.onBlur] - Optional function to handle input blur events.
 * @param {string} [props.className=''] - Optional additional CSS class names to apply to the input.
 * @param {string} [props.id=''] - Optional additional id to apply to the input.
 * @param {Function} [props.handleKeyDown] - Block decimal & non-integer keys.
 * @param {Function} [props.onInputChange] - Function to handle input change events.
 * @param {boolean} [props.isDark] - Legacy prop for compatibility (ignored).
 * @param {string} [props.feedbackType] - Legacy prop for compatibility (ignored).
 * @param {string} [props.inputSize] - Legacy prop for compatibility (ignored).
 * @param {string} [props.autocomplete] - The autocomplete attribute for the input.
 * @param {Function} [props.onUpdate] - Legacy callback pattern (calls onChange with value).
 * @returns {JSX.Element} The rendered MUI TextField component.
 */

const TextInput = ({
  value,
  onChange,
  label,
  required = false,
  disabled = false,
  maxLength,
  type = 'text',
  error,
  placeholderText = '',
  helperText = '',
  onBlur,
  className = '',
  id,
  handleKeyDown,
  onInputChange,
  isDark, // Accept isDark prop but ignore it for compatibility
  feedbackType, // Accept feedbackType prop but ignore it for compatibility
  inputSize, // Accept inputSize prop but ignore it for compatibility
  autocomplete, // Pass through to MUI TextField
  onUpdate, // Legacy callback pattern
  ...props
}) => {
  // Acknowledge legacy props for backward compatibility
  void isDark
  void feedbackType
  void inputSize

  return (
    <div className="text-input-w-25">
      {label && (
        <label
          htmlFor={`text-input` + (id ? `_${id}` : '')}
          style={{
            display: 'block',
            marginBottom: '4px',
            fontSize: '12px',
            fontWeight: '400',
            color: disabled ? 'var(--color-text-secondary, #9ca3af)' : 'var(--color-text-secondary, #d1d5db)',
          }}
        >
          {label}
          {required && <span className="sec-color pl-0.5">*</span>}
        </label>
      )}
      <TextField
        data-testid={`text-input` + (id ? `_${id}` : '')}
        id={`text-input` + (id ? `_${id}` : '')}
        value={value ?? ''}
        onChange={e => {
          if (onChange) onChange(e.target.value)
          if (onUpdate) onUpdate(e.target.value) // Legacy callback pattern
          if (onInputChange) onInputChange(e)
        }}
        required={required}
        disabled={disabled}
        inputProps={{maxLength}}
        type={type}
        error={!!error}
        helperText={error || helperText}
        placeholder={placeholderText}
        onBlur={onBlur}
        className={`custom-text-field ${className}`}
        onKeyDown={handleKeyDown}
        variant="outlined"
        fullWidth
        size="small"
        autoComplete={autocomplete}
        sx={{
          '& .MuiInputBase-root': {
            '& input::placeholder': {
              color: '#616161 !important',
              opacity: 1,
            },
            '& .MuiOutlinedInput-input::placeholder': {
              color: '#616161 !important',
              opacity: 1,
            },
          },
          '& .MuiInputBase-root.Mui-disabled': {
            backgroundColor: '#818181 !important',
            borderColor: '#818181 !important',
            color: '#616161 !important',

            '& input': {
              color: '#616161 !important',
              WebkitTextFillColor: '#616161 !important',
              '&::placeholder': {
                color: '#616161 !important',
                opacity: 1,
              },
            },

            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#818181 !important',
            },
          },
        }}
        {...props}
      />
    </div>
  )
}

export default TextInput
